import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, PlusIcon, ImageIcon, MailIcon, LinkedinIcon, InstagramIcon, XIcon, BarChart3Icon } from 'lucide-react';
import { addMonths, eachDayOfInterval, endOfMonth, endOfWeek, format, isSameDay, isSameMonth, startOfMonth, startOfWeek } from 'date-fns';
import { useWorkspace } from '../store/workspaceStore';
import { SocialChannel, SocialPost, SocialStatus } from '../types';
import { Topbar } from '../components/layout/Topbar';
const CHANNEL_META: Record<SocialChannel, {
  label: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
  cls: string;
  dot: string;
}> = {
  flyer: {
    label: 'Flyer',
    icon: ImageIcon,
    cls: 'bg-orange-100 text-orange-700',
    dot: 'bg-orange-500'
  },
  email: {
    label: 'Email',
    icon: MailIcon,
    cls: 'bg-sky-100 text-sky-700',
    dot: 'bg-sky-500'
  },
  linkedin: {
    label: 'LinkedIn',
    icon: LinkedinIcon,
    cls: 'bg-blue-100 text-blue-700',
    dot: 'bg-blue-600'
  },
  instagram: {
    label: 'Instagram',
    icon: InstagramIcon,
    cls: 'bg-pink-100 text-pink-700',
    dot: 'bg-pink-500'
  }
};
const STATUS_META: Record<SocialStatus, string> = {
  planned: 'bg-slate-100 text-slate-500',
  scheduled: 'bg-amber-100 text-amber-700',
  posted: 'bg-emerald-100 text-emerald-700'
};
export function SocialPlanner() {
  const social = useWorkspace(s => s.social);
  const [cursor, setCursor] = useState(new Date());
  const [addOpen, setAddOpen] = useState(false);
  const [addDate, setAddDate] = useState<string | null>(null);
  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(cursor));
    const end = endOfWeek(endOfMonth(cursor));
    return eachDayOfInterval({
      start,
      end
    });
  }, [cursor]);
  const openAdd = (date?: string) => {
    setAddDate(date ?? format(new Date(), 'yyyy-MM-dd'));
    setAddOpen(true);
  };
  return <div className="flex min-h-full flex-1 flex-col" data-id="element-335" data-mp="2nq5imenjfh8q">
      <Topbar title="Social Planner" subtitle="Plan and track flyers, email, LinkedIn & Instagram — connected to each deal." action={<button onClick={() => openAdd()} className="inline-flex items-center gap-2 rounded-lg bg-brand-accent px-3.5 py-2 text-sm font-semibold text-white hover:bg-brand" data-id="element-337" data-mp="wl2anl1rn2ap">
            <PlusIcon className="h-4 w-4" data-id="element-338" data-mp="15dx5dktlxfm8" /> Add Post
          </button>} data-id="element-336" data-mp="ro22n4lcvpgy" />

      <div className="mp-scroll flex-1 overflow-y-auto p-6" data-id="element-339" data-mp="8pppl7rbpvg3">
        {/* Channel legend + month nav */}
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3" data-id="element-340" data-mp="dp2cttm3x4j7">
          <div className="flex items-center gap-2" data-id="element-341" data-mp="hp4hmbn52e2p">
            <button onClick={() => setCursor(c => addMonths(c, -1))} className="rounded-lg border border-slate-200 bg-white p-2 hover:bg-slate-50" data-id="element-342" data-mp="hrqp6ba14tkx">
              <ChevronLeftIcon className="h-4 w-4 text-slate-500" data-id="element-343" data-mp="1t5kq1l09zug2" />
            </button>
            <h2 className="w-40 text-center text-base font-bold text-slate-900" data-id="element-344" data-mp="1y3cs37umwwxz">{format(cursor, 'MMMM yyyy')}</h2>
            <button onClick={() => setCursor(c => addMonths(c, 1))} className="rounded-lg border border-slate-200 bg-white p-2 hover:bg-slate-50" data-id="element-345" data-mp="2882nsvzg34tr">
              <ChevronRightIcon className="h-4 w-4 text-slate-500" data-id="element-346" data-mp="23asxzf858r5e" />
            </button>
          </div>
          <div className="flex flex-wrap gap-3" data-id="element-347" data-mp="1yfn49u8i286r">
            {Object.entries(CHANNEL_META).map(([k, m]) => <span key={k} className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500" data-id="element-348" data-mp="1phbic5ikvykn">
                <span className={`h-2.5 w-2.5 rounded-full ${m.dot}`} data-id="element-349" data-mp="1kii1c3431pfq" /> {m.label}
              </span>)}
          </div>
        </div>

        {/* Calendar grid */}
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-card" data-id="element-350" data-mp="1fkyp18qy3mqf">
          <div className="grid grid-cols-7 border-b border-slate-100 bg-slate-50" data-id="element-351" data-mp="1bjwpeqt498nx">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => <div key={d} className="px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-400" data-id="element-352" data-mp="35ww8itwpgkzy">{d}</div>)}
          </div>
          <div className="grid grid-cols-7" data-id="element-353" data-mp="3hye3401mtntp">
            {days.map(day => {
            const key = format(day, 'yyyy-MM-dd');
            const posts = social.filter(p => p.date === key);
            const inMonth = isSameMonth(day, cursor);
            const today = isSameDay(day, new Date());
            return <div key={key} className={`group min-h-[112px] border-b border-r border-slate-100 p-1.5 ${inMonth ? 'bg-white' : 'bg-slate-50/60'}`} data-id="element-354" data-mp="2u6p460ub213s">
                  <div className="mb-1 flex items-center justify-between px-1" data-id="element-355" data-mp="2kbov99k90gzx">
                    <span className={`text-xs font-semibold ${today ? 'flex h-5 w-5 items-center justify-center rounded-full bg-brand-accent text-white' : inMonth ? 'text-slate-600' : 'text-slate-300'}`} data-id="element-356" data-mp="2pa8fn5j71kq6">
                      {format(day, 'd')}
                    </span>
                    <button onClick={() => openAdd(key)} className="opacity-0 transition group-hover:opacity-100" data-id="element-357" data-mp="1wm72iyx97i12">
                      <PlusIcon className="h-3.5 w-3.5 text-slate-400 hover:text-brand-accent" data-id="element-358" data-mp="1rof920gynnur" />
                    </button>
                  </div>
                  <div className="space-y-1" data-id="element-359" data-mp="25lan67c2k5ho">
                    {posts.map(p => {
                  const m = CHANNEL_META[p.channel];
                  const Icon = m.icon;
                  return <div key={p.id} className={`flex items-center gap-1 rounded-md px-1.5 py-1 text-[11px] font-medium ${m.cls}`} data-id="element-360" data-mp="2amrmhyewnjw5">
                          <Icon className="h-3 w-3 shrink-0" data-id="element-361" data-mp="2pacotq3yhf30" />
                          <span className="truncate" data-id="element-362" data-mp="2kcjjezah8p3s">{p.title}</span>
                        </div>;
                })}
                  </div>
                </div>;
          })}
          </div>
        </div>

        {/* Posted metrics */}
        <PostedMetrics data-id="element-363" data-mp="2tawnwz83asrs" />
      </div>

      <AnimatePresence data-id="element-364" data-mp="1r03b6vgvmsag">{addOpen && <AddPostModal date={addDate!} onClose={() => setAddOpen(false)} data-id="element-365" data-mp="1r0uvvb8xr5ly" />}</AnimatePresence>
    </div>;
}
function PostedMetrics() {
  const social = useWorkspace(s => s.social);
  const posted = social.filter(p => p.status === 'posted' && p.metrics);
  if (posted.length === 0) return null;
  return <div className="mt-6" data-id="element-366" data-mp="24sj6tb7g7o1c">
      <h3 className="mb-3 flex items-center gap-1.5 text-sm font-bold text-slate-700" data-id="element-367" data-mp="24st1vd7coldw">
        <BarChart3Icon className="h-4 w-4 text-brand-accent" data-id="element-368" data-mp="29s5n5tikpked" /> Recently Posted — Metrics
      </h3>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2" data-id="element-369" data-mp="2er8szg6dnune">
        {posted.map(p => {
        const m = CHANNEL_META[p.channel];
        const Icon = m.icon;
        return <div key={p.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-card" data-id="element-370" data-mp="2np17wj86t0a9">
              <div className="flex items-center gap-2" data-id="element-371" data-mp="pc10m3rp5ydz">
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${m.cls}`} data-id="element-372" data-mp="tb1f5a356ev6">
                  <Icon className="h-3 w-3" data-id="element-373" data-mp="ltv2ejqan7jt" /> {m.label}
                </span>
                <p className="truncate text-sm font-semibold text-slate-800" data-id="element-374" data-mp="h0iqna6pqv8h">{p.title}</p>
              </div>
              {p.example && <p className="mt-1.5 text-xs text-slate-500" data-id="element-375" data-mp="c30ivn4fmm22">{p.example}</p>}
              <div className="mt-3 grid grid-cols-3 gap-2 text-center" data-id="element-376" data-mp="81wziqn6e7w3">
                <Metric label="Impressions" value={p.metrics?.impressions} data-id="element-377" data-mp="14qwdmmafy3w7" />
                <Metric label="Engagements" value={p.metrics?.engagements} data-id="element-378" data-mp="14os85zww8wtt" />
                <Metric label="Clicks" value={p.metrics?.clicks} data-id="element-379" data-mp="zsupvo22lgee" />
              </div>
            </div>;
      })}
      </div>
    </div>;
}
function Metric({
  label,
  value
}: {
  label: string;
  value?: number;
}) {
  return <div className="rounded-lg bg-slate-50 py-2" data-id="element-380" data-mp="3ggfe7b7mgloo">
      <p className="text-base font-bold text-slate-900" data-id="element-381" data-mp="3gia1gw6d8pvx">{value != null ? value.toLocaleString() : '—'}</p>
      <p className="text-[10px] uppercase tracking-wide text-slate-400" data-id="element-382" data-mp="3bg17pxlo5jsr">{label}</p>
    </div>;
}
function AddPostModal({
  date,
  onClose
}: {
  date: string;
  onClose: () => void;
}) {
  const proposals = useWorkspace(s => s.proposals);
  const addSocialPost = useWorkspace(s => s.addSocialPost);
  const currentUserId = useWorkspace(s => s.currentUserId);
  const marketing = useWorkspace(s => s.people.find(p => p.role === 'marketing'));

  // Only deals past ELA/OM are ready for social planning
  const ready = proposals.filter(p => ['om_sent', 'ela_signed', 'listed', 'live'].includes(p.stage));
  const [title, setTitle] = useState('');
  const [channel, setChannel] = useState<SocialChannel>('flyer');
  const [when, setWhen] = useState(date);
  const [proposalId, setProposalId] = useState(ready[0]?.id ?? '');
  const [status, setStatus] = useState<SocialStatus>('planned');
  const submit = () => {
    if (!title.trim()) return;
    addSocialPost({
      title: title.trim(),
      channel,
      date: when,
      status,
      ownerId: marketing?.id ?? currentUserId,
      proposalId: proposalId || undefined
    });
    onClose();
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose} data-id="element-383" data-mp="5feuc53m67x3">
      <motion.div initial={{
      opacity: 0,
      scale: 0.96,
      y: 8
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.96
    }} className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-pop" onClick={e => e.stopPropagation()} data-id="element-384" data-mp="j6l1mnd4x9uj">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4" data-id="element-385" data-mp="3iokknnxytlmu">
          <h2 className="text-base font-bold text-slate-900" data-id="element-386" data-mp="i6be2an5zus">Add Social Post</h2>
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100" data-id="element-387" data-mp="3rmmsong49ejm"><XIcon className="h-5 w-5" data-id="element-388" data-mp="1bw0i3snkjuxu" /></button>
        </div>
        <div className="space-y-4 px-6 py-5" data-id="element-389" data-mp="1byd18952cbch">
          <label className="block" data-id="element-390" data-mp="16y8l4k82hims">
            <span className="mb-1 block text-xs font-semibold text-slate-600" data-id="element-391" data-mp="16x78ibbeko2y">Linked deal (OM sent / ELA signed)</span>
            <select value={proposalId} onChange={e => setProposalId(e.target.value)} className={inp} data-id="element-392" data-mp="33wkmek9hq7zt">
              <option value="" data-id="element-393" data-mp="2u4x1z3bcpr7u">— Standalone —</option>
              {ready.map(p => <option key={p.id} value={p.id} data-id="element-394" data-mp="2u33vbo0qn6zu">{p.property}</option>)}
            </select>
          </label>
          <label className="block" data-id="element-395" data-mp="2q18uekmpyvml">
            <span className="mb-1 block text-xs font-semibold text-slate-600" data-id="element-396" data-mp="2l4rt0oqiekrk">Title</span>
            <input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Harborview — Launch Flyer" className={inp} autoFocus data-id="element-397" data-mp="2g5f8ian41ps5" />
          </label>
          <div className="grid grid-cols-2 gap-4" data-id="element-398" data-mp="2b6cavb9gmlu7">
            <label className="block" data-id="element-399" data-mp="2758ywzuj99y9">
              <span className="mb-1 block text-xs font-semibold text-slate-600" data-id="element-400" data-mp="276aar6jdi0h1">Channel</span>
              <select value={channel} onChange={e => setChannel(e.target.value as SocialChannel)} className={inp} data-id="element-401" data-mp="9e61jrw0a0j6">
                <option value="flyer" data-id="element-402" data-mp="5azu4gx5q3b4">Flyer</option>
                <option value="email" data-id="element-403" data-mp="1nbinuhqwxu24">Email Campaign</option>
                <option value="linkedin" data-id="element-404" data-mp="1s9u0t1ci8kok">LinkedIn</option>
                <option value="instagram" data-id="element-405" data-mp="1s8969bq6x1qp">Instagram</option>
              </select>
            </label>
            <label className="block" data-id="element-406" data-mp="1x12bs1thge3u">
              <span className="mb-1 block text-xs font-semibold text-slate-600" data-id="element-407" data-mp="2124bskv84u6v">Date</span>
              <input type="date" value={when} onChange={e => setWhen(e.target.value)} className={inp} data-id="element-408" data-mp="213yl11x4uxv4" />
            </label>
          </div>
          <label className="block" data-id="element-409" data-mp="19a6cykqm0in3">
            <span className="mb-1 block text-xs font-semibold text-slate-600" data-id="element-410" data-mp="19b80hootaxlp">Status</span>
            <select value={status} onChange={e => setStatus(e.target.value as SocialStatus)} className={inp} data-id="element-411" data-mp="1db00ve41xxfz">
              <option value="planned" data-id="element-412" data-mp="1icxn8h7r18d5">Planned</option>
              <option value="scheduled" data-id="element-413" data-mp="2oshtbhg6nan7">Scheduled</option>
              <option value="posted" data-id="element-414" data-mp="2ou2on9abmxm4">Posted</option>
            </select>
          </label>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-slate-100 bg-slate-50 px-6 py-4" data-id="element-415" data-mp="2tsme3jo4irj8">
          <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100" data-id="element-416" data-mp="pmyimpuhdgfg">Cancel</button>
          <button onClick={submit} disabled={!title.trim()} className="rounded-lg bg-brand-accent px-4 py-2 text-sm font-semibold text-white hover:bg-brand disabled:opacity-40" data-id="element-417" data-mp="13loj8fgidy8k">Add to Calendar</button>
        </div>
      </motion.div>
    </div>;
}
const inp = 'w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand-accent';